import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";

type ExamTypeRow = Database["public"]["Tables"]["exam_types"]["Row"];
type ExamTypeInsert = Database["public"]["Tables"]["exam_types"]["Insert"];

interface UseExamTypesOptions {
  searchTerm?: string;
  includeInactive?: boolean;
  enabled?: boolean;
}

export const useExamTypes = ({
  searchTerm,
  includeInactive = false,
  enabled = true,
}: UseExamTypesOptions = {}) => {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["exam-types", customerId, searchTerm, includeInactive],
    enabled: enabled && !!customerId,
    queryFn: async () => {
      if (!customerId) throw new Error("Nenhuma clínica selecionada");
      let query = supabase
        .from("exam_types")
        .select("*")
        .order("name", { ascending: true })
        .or(`customer_id.is.null,customer_id.eq.${customerId}`);

      if (!includeInactive) {
        query = query.eq("is_active", true);
      }

      if (searchTerm && searchTerm.trim().length >= 2) {
        query = query.ilike("name", `%${searchTerm.trim()}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ExamTypeRow[];
    },
  });
};

export const useCreateExamType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Omit<ExamTypeInsert, "customer_id">) => {
      const customerId = await getCurrentClinicCustomerId();
      const { data, error } = await supabase
        .from("exam_types")
        .insert({
          ...payload,
          name: payload.name.trim(),
          description: payload.description?.trim() || null,
          category: payload.category?.trim() || null,
          is_active: payload.is_active ?? true,
          customer_id: customerId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exam-types"] });
      toast.success("Exame cadastrado no catálogo");
    },
    onError: (error) => {
      console.error("Erro ao cadastrar exame:", error);
      toast.error("Não foi possível cadastrar o exame");
    },
  });
};
